/**
 * Utility functions for calculating fighter scores based on position and Moneyline odds
 */

// Multipliers for each position in the lineup
const POSITION_MULTIPLIERS = [0, 0.2, 0.4, 0.6, 0.8];

/**
 * Calculate score for a fighter in a specific lineup slot
 *
 * @param {number} moneyline - Fighter's moneyline odds (e.g. -250, +150)
 * @param {number} position - Position in lineup (0-based index)
 * @returns {number} - Calculated score
 */
export const calculateScoreForSlot = (moneyline, position) => {
  // Ensure position is within valid range
  if (position < 0 || position >= POSITION_MULTIPLIERS.length) {
    console.warn(
      `Invalid position: ${position}. Using default multiplier of 0%.`
    );
    position = 4; // Default to last position if invalid
  }

  const multiplier = POSITION_MULTIPLIERS[position];

  // If the fighter is a very strong favorite (-1000 or less), their score is 0
  if (moneyline <= -1000) {
    return 0;
  }

  // Calculate base score from moneyline
  let baseScore;
  if (moneyline > 0) {
    // Underdog: (10 × (moneyline / 1000)) + 10
    baseScore = 10 * (moneyline / 1000) + 10;
  } else {
    // Favorite: 10 - (10 × (Math.abs(moneyline) / 1000))
    baseScore = 10 - 10 * (Math.abs(moneyline) / 1000);
  }

  // Apply the penalty based on the multiplier
  const penalty = baseScore * multiplier;
  const finalScore = baseScore - penalty;

  // Round to 2 decimal places
  return Math.round(finalScore * 100) / 100;
};

/**
 * Calculate all possible scores for a fighter in all positions
 *
 * @param {number} moneyline - Fighter's moneyline odds
 * @param {number} lotnPosition - Which position is this fighter in (for LOTN calculation)
 * @returns {Array} - Array of scores for all positions
 */
export const calculateScores = (moneyline, lotnPosition = null) => {
  return POSITION_MULTIPLIERS.map((_, position) => {
    let score = calculateScoreForSlot(moneyline, position);
    // Double score if this is the LOTN position
    if (lotnPosition !== null && position === lotnPosition) {
      score *= 2;
    }
    return score;
  });
};

/**
 * Check if a fighter or their opponent is already in the lineup
 *
 * @param {number} fighterIndex - Index of the fighter in the fighters array
 * @param {Array} lineup - Current lineup
 * @param {Array} fighters - Array of all fighters
 * @returns {boolean} - True if fighter or opponent is in lineup
 */
export const isFighterOrOpponentInLineup = (fighterIndex, lineup, fighters) => {
  if (!fighters || fighterIndex < 0 || fighterIndex >= fighters.length) {
    return false;
  }

  const fighter = fighters[fighterIndex];

  // Find opponent index (assumes fighters are paired sequentially in the array)
  const opponentIndex =
    fighterIndex % 2 === 0 ? fighterIndex + 1 : fighterIndex - 1;

  // Check if either fighter or opponent is in lineup
  return lineup.some(
    (selectedFighter) =>
      selectedFighter &&
      (selectedFighter.FighterId === fighter.FighterId ||
        (opponentIndex >= 0 &&
          opponentIndex < fighters.length &&
          selectedFighter.FighterId === fighters[opponentIndex].FighterId))
  );
};
